import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2, Search, Users } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const etiquetasDisponibles = ['Importador', 'Premium', 'Alto volumen'];

const Compradores = () => {
  const [compradores, setCompradores] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '', empresa: '', whatsapp: '', ciudad: '', pais: '', etiquetas: [], estado: 'Activo', notas: '',
  });

  useEffect(() => { loadCompradores(); }, []);
  useEffect(() => { applyFilters(); }, [compradores, searchTerm, estadoFilter]);

  const loadCompradores = async () => {
    try {
      const res = await axios.get(`${API}/compradores`);
      setCompradores(res.data);
    } catch {
      toast.error('Error al cargar compradores');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let f = [...compradores];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      f = f.filter(c =>
        c.nombre?.toLowerCase().includes(term) ||
        c.empresa?.toLowerCase().includes(term) ||
        c.pais?.toLowerCase().includes(term)
      );
    }
    if (estadoFilter !== 'all') f = f.filter(c => c.estado === estadoFilter);
    setFiltered(f);
  };

  const handleOpenDialog = (comprador = null) => {
    if (comprador) {
      setEditingId(comprador.id);
      setFormData(comprador);
    } else {
      setEditingId(null);
      setFormData({ nombre: '', empresa: '', whatsapp: '', ciudad: '', pais: '', etiquetas: [], estado: 'Activo', notas: '' });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.nombre) { toast.error('El nombre es requerido'); return; }
    try {
      if (editingId) {
        await axios.put(`${API}/compradores/${editingId}`, formData);
        toast.success('Comprador actualizado');
      } else {
        await axios.post(`${API}/compradores`, formData);
        toast.success('Comprador creado');
      }
      setShowDialog(false);
      loadCompradores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/compradores/${id}`);
      toast.success('Comprador eliminado');
      loadCompradores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
    setDeleteId(null);
  };

  const toggleEtiqueta = (tag) => {
    const current = formData.etiquetas || [];
    setFormData({
      ...formData,
      etiquetas: current.includes(tag) ? current.filter(e => e !== tag) : [...current, tag]
    });
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="text-muted-foreground">Cargando...</div></div>;

  return (
    <div className="space-y-6 bg-background min-h-screen">
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">Compradores</h1>
              <p className="text-muted-foreground mt-1">{compradores.length} compradores registrados</p>
            </div>
            <Button onClick={() => handleOpenDialog()} data-testid="new-comprador-button">
              <Plus className="h-4 w-4 mr-2" /> Nuevo Comprador
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Card className="p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Buscar por nombre, empresa o país..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
            </div>
            <Select value={estadoFilter} onValueChange={setEstadoFilter}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Estado" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="Activo">Activo</SelectItem>
                <SelectItem value="Bloqueado">Bloqueado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {filtered.length === 0 ? (
          <Card className="p-12 text-center">
            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No hay compradores</p>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filtered.map((comp) => (
              <Card key={comp.id} className="p-4">
                <div className="flex flex-col sm:flex-row gap-4 justify-between">
                  <div className="flex-1">
                    <div className="flex items-start gap-3 mb-2">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold">{comp.nombre}</h3>
                        {comp.empresa && <p className="text-sm text-muted-foreground">{comp.empresa}</p>}
                      </div>
                      <Badge variant={comp.estado === 'Activo' ? 'default' : 'destructive'}>{comp.estado}</Badge>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-2 text-sm mb-2">
                      {comp.whatsapp && <div><span className="text-muted-foreground">WhatsApp:</span> {comp.whatsapp}</div>}
                      {comp.ciudad && <div><span className="text-muted-foreground">Ciudad:</span> {comp.ciudad}</div>}
                      {comp.pais && <div><span className="text-muted-foreground">País:</span> {comp.pais}</div>}
                    </div>
                    {comp.etiquetas?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {comp.etiquetas.map(tag => <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>)}
                      </div>
                    )}
                  </div>
                  <div className="flex sm:flex-col gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleOpenDialog(comp)}><Edit className="h-4 w-4" /></Button>
                    <Button variant="outline" size="sm" onClick={() => setDeleteId(comp.id)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar' : 'Nuevo'} Comprador</DialogTitle>
            <DialogDescription>Completá la información del comprador</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Nombre *</Label><Input value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} /></div>
              <div className="space-y-2"><Label>Empresa</Label><Input value={formData.empresa} onChange={(e) => setFormData({ ...formData, empresa: e.target.value })} /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>WhatsApp</Label><Input placeholder="+971 50 123 4567" value={formData.whatsapp} onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })} /></div>
              <div className="space-y-2"><Label>Ciudad</Label><Input value={formData.ciudad} onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })} /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>País</Label><Input value={formData.pais} onChange={(e) => setFormData({ ...formData, pais: e.target.value })} /></div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={formData.estado} onValueChange={(v) => setFormData({ ...formData, estado: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Activo">Activo</SelectItem>
                    <SelectItem value="Bloqueado">Bloqueado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Etiquetas</Label>
              <div className="flex flex-wrap gap-2">
                {etiquetasDisponibles.map(tag => (
                  <Badge key={tag} variant={(formData.etiquetas || []).includes(tag) ? 'default' : 'outline'} className="cursor-pointer" onClick={() => toggleEtiqueta(tag)}>{tag}</Badge>
                ))}
              </div>
            </div>
            <div className="space-y-2"><Label>Notas</Label><Textarea rows={3} value={formData.notas} onChange={(e) => setFormData({ ...formData, notas: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={handleSave}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar comprador?</AlertDialogTitle>
            <AlertDialogDescription>El comprador será eliminado. Su nombre seguirá visible en ofertas existentes.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteId)}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Compradores;
